import { CheckCircle } from '@mui/icons-material';
import { Box, FormHelperText, IconButton, Paper } from "@mui/material";
import React, { ChangeEvent, useEffect, useMemo, useState } from "react";
import { userEmailVerificationPath } from "../../../functions/src/admin/paths";
import { auth, DataSnapshot, functions } from "../../api/firebase";
import { useDatabaseVal, useList, useSnapshotState } from "../../api/firebaseHooks";
import { VerificationData } from "../../types";
import { getProfErrorDlgMsg, useOrderAppStrings } from "../order-app-strings";
import { checkProfile, PROFILE_EMAIL_VERIFICATION_STATUS_ALERT_ID, PROFILE_VALIDATION_SUCCESS, profileFieldAnchorId, profileValidationFocusTargetId } from "../validation";
import AccountTabs from "./AccountTabs";
import EmailEditDialog from "./EmailEditDialog";
import EmailVerification from "./EmailVerification";
import EmailVerificationActions from "./EmailVerificationActions";
import { ProfileEditableField } from "./inputs";
import OrderHistory from "./OrderHistory";
import { PROFILE_TAB_ID, PROFILE_TAB_PANEL_ID, type ProfileTab } from "./profileTabs";
import Students from "./Students";
import { useUserProfileStrings } from "./userProfile-strings";
import WalletHistory from "./WalletHistory";
import { usePopulateEmailVerification } from "./populateEmailVerificationIfNeeded";

type UserProfileCompProps = {
    profSnapshot: DataSnapshot;
    students: DataSnapshot;
    onFinish: () => void;
    profileTab: ProfileTab;
    onProfileTabChange: (tab: ProfileTab) => void;
    /** Incremented when navigation blocks order/packages or forces personal tab — scroll to first validation issue once. */
    profileValidationScrollRequest?: number;
};

export default function UserProfileComp({ profSnapshot, students, profileTab, onProfileTabChange, profileValidationScrollRequest = 0 }: UserProfileCompProps) {

    const profileStrings = useUserProfileStrings();
    const orderAppStrings = useOrderAppStrings();
    const emailAuth = auth.currentUser?.email ?? '';
    const [email, setEmail] = useSnapshotState(profSnapshot, 'email');
    const [mobile] = useSnapshotState(profSnapshot, 'mobile');
    const [verificationError, setVerificationError] = useState(''); // State for specific verification error
    const [hasCheckedVerification, setHasCheckedVerification] = useState(false); // State to track if user has checked verification
    const [successType, setSuccessType] = useState<'email_sent' | 'email_verified' | null>(null); // State for success type
    const [emailEditDialogOpen, setEmailEditDialogOpen] = useState(false); // State for email edit dialog
    const [activeEditField, setActiveEditField] = useState<string | null>(null);

    // Check if user is registered with Google
    const isGoogleUser = useMemo(() => {
        return auth.currentUser?.providerData?.some(provider => provider.providerId === 'google.com') ?? false;
    }, []);

    const currentUserUid = auth.currentUser?.uid;
    const [emailVerificationData, emailVerificationSnapshot] = useDatabaseVal<VerificationData>(userEmailVerificationPath(currentUserUid));
    usePopulateEmailVerification(currentUserUid, emailVerificationSnapshot, emailVerificationData, isGoogleUser);

    useEffect(() => {
        if (email !== undefined && !email && emailAuth) {
            const firstName = String(profSnapshot.child('firstName').val() ?? '').trim();
            const lastName = String(profSnapshot.child('lastName').val() ?? '').trim();
            if (firstName && lastName) {
                setEmail(emailAuth);
            }
        }
    }, [email, emailAuth, profSnapshot]);

    const studentsList = useList(students);
    
    const profileOK = useMemo(() => checkProfile(profSnapshot?.val(), studentsList, emailVerificationData), [profSnapshot, students, emailVerificationData]);
    const profileFieldErrorMessage = profileOK != null && profileOK !== PROFILE_VALIDATION_SUCCESS
        ? getProfErrorDlgMsg(profileOK, orderAppStrings)
        : undefined;
    const validationFocusId = useMemo(() => {
        if (profileOK == null || profileOK === PROFILE_VALIDATION_SUCCESS) {
            return null;
        }
        return profileValidationFocusTargetId(profileOK, studentsList);
    }, [profileOK, studentsList]);

    useEffect(() => {
        if (!profileValidationScrollRequest || !validationFocusId) {
            return;
        }
        const target = document.getElementById(validationFocusId);
        const scrollBehavior = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
            ? 'auto'
            : 'smooth';
        target?.scrollIntoView({ behavior: scrollBehavior, block: 'nearest' });
    }, [profileValidationScrollRequest, validationFocusId]);

    const onChangeTrimmed = ({ target: { name, value } }: ChangeEvent<HTMLInputElement>) => {
        profSnapshot.ref.child(name).set(value.trim());
    };

    const marketingEmail = (email || emailAuth).trim();
    const showEmailVerificationExtras = !isGoogleUser && emailVerificationData?.verified !== true;
    const emailDisplayValue = String(profSnapshot.child('email').val() ?? '').trim();
    const emailVerificationStatusAlertVisible = showEmailVerificationExtras && (
        !!verificationError || hasCheckedVerification || successType === 'email_sent' || successType === 'email_verified'
    );
    const emailFieldDescribedBy = (() => {
        const ids: string[] = [];
        if (validationFocusId === profileFieldAnchorId('email') && profileFieldErrorMessage) {
            ids.push(`${profileFieldAnchorId('email')}-validation-error`);
        }
        if (emailVerificationStatusAlertVisible) {
            ids.push(PROFILE_EMAIL_VERIFICATION_STATUS_ALERT_ID);
        }
        return ids.length ? ids.join(' ') : undefined;
    })();

    const syncBrevoFirstName = (firstName: string): void => {
        const trimmed = firstName.trim();
        if (!marketingEmail || !trimmed) {
            return;
        }
        // Update only Brevo (latest version)
        void functions.httpsCallable('updateBrevoContactFirstName')({ firstName: trimmed }).catch((error: unknown) => {
            console.error('updateBrevoContactFirstName failed:', error);
        });
    };

    const syncBrevoLastName = (lastName: string): void => {
        const trimmed = lastName.trim();
        if (!marketingEmail || !trimmed) {
            return;
        }
        // Update only Brevo (latest version)
        void functions.httpsCallable('updateBrevoContactLastName')({ lastName: trimmed }).catch((error: unknown) => {
            console.error('updateBrevoContactLastName failed:', error);
        });
    };

    const [role] = useSnapshotState(profSnapshot, 'role');

    const handlePhoneNumberChange = (newNumber: string) => {
        profSnapshot.ref.child('mobile').set(newNumber);
    };

    const handleEmailUpdated = (newEmail: string) => {
        // Update the local state to reflect the new email
        setEmail(newEmail);
        // Clear any verification errors since email changed
        setVerificationError('');
        setHasCheckedVerification(false);
        setSuccessType(null);
    };

    // Safety check - don't render if user is not authenticated (after all hooks)
    if (!auth.currentUser) {
        return null;
    }

    return <div className='base_margin_setup account-tabs-root'>
        <AccountTabs value={profileTab} onChange={onProfileTabChange} />

        <Box className="account-tabs-root__panel" role="presentation">
            <Box
                component="section"
                role="tabpanel"
                id={PROFILE_TAB_PANEL_ID.personal}
                aria-labelledby={PROFILE_TAB_ID.personal}
                hidden={profileTab !== 'personal'}
                className="account-tabs-root__tabpanel"
            >
            {profileTab === 'personal' && (
                <Students snapshot={students} profileValidationFocusId={validationFocusId} profileValidationErrorMessage={profileFieldErrorMessage}>
                    {({ dialogs, personnelBlock, studentsSection }) => (
                        <div className="account-personal-layout">
                            {dialogs}
                            <section>
                                <h2 className="account-figma-section-heading">{profileStrings.personalInfoHeading}</h2>
                                <div className="account-figma-card">
                                    <ProfileEditableField variant="figma" name='firstName' label={profileStrings.firstName} snapshot={profSnapshot} activeEditField={activeEditField} onEditFieldChange={setActiveEditField} onSaved={syncBrevoFirstName} requiredEmptyMessage={orderAppStrings.profileErrorFname} validationInvalid={validationFocusId === profileFieldAnchorId('firstName')} validationErrorMessage={validationFocusId === profileFieldAnchorId('firstName') ? profileFieldErrorMessage : undefined} />
                                    <ProfileEditableField variant="figma" name='lastName' label={profileStrings.lastName} snapshot={profSnapshot} activeEditField={activeEditField} onEditFieldChange={setActiveEditField} onSaved={syncBrevoLastName} requiredEmptyMessage={orderAppStrings.profileErrorLname} validationInvalid={validationFocusId === profileFieldAnchorId('lastName')} validationErrorMessage={validationFocusId === profileFieldAnchorId('lastName') ? profileFieldErrorMessage : undefined} />

                                    <div
                                        id={profileFieldAnchorId('email')}
                                        tabIndex={-1}
                                        className={`account-figma-field account-figma-field--email${validationFocusId === profileFieldAnchorId('email') ? ' account-figma-field--validation-error' : ''}${!isGoogleUser && emailVerificationData?.verified === true ? ' account-figma-field--email-verified' : ''}${showEmailVerificationExtras ? ' account-figma-field--with-email-extras' : ''}`}
                                        aria-invalid={validationFocusId === profileFieldAnchorId('email') ? true : undefined}
                                        aria-describedby={emailFieldDescribedBy}
                                    >
                                        {showEmailVerificationExtras && (
                                            <div className="account-figma-email-extras__verification account-figma-email-extras__verification--above-email">
                                                <EmailVerification isEmailVerified={emailVerificationData?.verified === true} verificationError={verificationError} hasCheckedVerification={hasCheckedVerification} successType={successType} />
                                            </div>
                                        )}
                                        <div className="account-figma-field__email-head">
                                            <div className="account-figma-field__content">
                                                <div className="account-figma-field__label-row">
                                                    <span id="account-profile-email-label" className="account-figma-field__label account-figma-field__label--required">{profileStrings.email}</span>
                                                </div>
                                                <div id="account-profile-email-value" className={`account-figma-field__value${!emailDisplayValue ? ' account-figma-field__value--empty' : ''}`} aria-labelledby="account-profile-email-label">{emailDisplayValue || profileStrings.profileFieldEmpty}</div>
                                            </div>
                                            {!isGoogleUser && (
                                                <Box className="account-figma-field__label-actions" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    {emailVerificationData?.verified === true && (
                                                        <span className="account-figma-field__verified">
                                                            <CheckCircle sx={{ fontSize: '14px' }} />
                                                            {profileStrings.verified}
                                                        </span>
                                                    )}
                                                    <IconButton
                                                        size="small"
                                                        className="account-figma-field__edit"
                                                        onClick={() => {
                                                            setActiveEditField(null);
                                                            setEmailEditDialogOpen(true);
                                                        }}
                                                        aria-label={profileStrings.editEmailTitle}
                                                    >
                                                        <img src="/img-assets/edit-orange.svg" alt="" className="account-figma-field__edit-img" />
                                                    </IconButton>
                                                </Box>
                                            )}
                                        </div>
                                        {validationFocusId === profileFieldAnchorId('email') && profileFieldErrorMessage && (
                                            <p id={`${profileFieldAnchorId('email')}-validation-error`} className="account-figma-field__validation-error" role="alert">
                                                {profileFieldErrorMessage}
                                            </p>
                                        )}
                                        {showEmailVerificationExtras && (
                                            <div className="account-figma-email-extras">
                                                <EmailVerificationActions emailVerification={emailVerificationData} setVerificationError={setVerificationError} setHasCheckedVerification={setHasCheckedVerification} setSuccessType={setSuccessType} />
                                                <FormHelperText className="account-figma-email-hint" component="p">
                                                    {profileStrings.emailSpamHint}
                                                </FormHelperText>
                                            </div>
                                        )}
                                    </div>
                                    <div className='separator account-figma-card__divider'></div>

                                    <ProfileEditableField variant="figma" name='mobile' label={profileStrings.mobile} snapshot={profSnapshot} activeEditField={activeEditField} onEditFieldChange={setActiveEditField} requiredEmptyMessage={orderAppStrings.profileErrorMobile} validationInvalid={validationFocusId === profileFieldAnchorId('mobile')} validationErrorMessage={validationFocusId === profileFieldAnchorId('mobile') ? profileFieldErrorMessage : undefined} />

                       {/* TODO: Uncomment this and comment the mobile input above, to allow phone verification */}
                                    {/* <PhoneVerification 
                            phoneNumber={mobile ?? ''} 
                            onPhoneNumberChange={handlePhoneNumberChange}
                        /> */}
                                    <div className='separator account-figma-card__divider'></div>
                                    {personnelBlock}
                                </div>
                            </section>

                            {studentsSection}
                        </div>
                    )}
                </Students>
            )}
            </Box>

            <Box
                component="section"
                role="tabpanel"
                id={PROFILE_TAB_PANEL_ID.orders}
                aria-labelledby={PROFILE_TAB_ID.orders}
                hidden={profileTab !== 'orders'}
                className="account-tabs-root__tabpanel"
            >
            {profileTab === 'orders' && (
                <Paper
                    variant="outlined"
                    className="account-history-card"
                    sx={{
                        borderRadius: 'var(--order_button_radius)',
                        borderColor: 'var(--row_border)',
                        p: '24px',
                    }}
                >
                    <OrderHistory />
                </Paper>
            )}
            </Box>

            <Box
                component="section"
                role="tabpanel"
                id={PROFILE_TAB_PANEL_ID.transactions}
                aria-labelledby={PROFILE_TAB_ID.transactions}
                hidden={profileTab !== 'transactions'}
                className="account-tabs-root__tabpanel"
            >
            {profileTab === 'transactions' && (
                <Paper
                    variant="outlined"
                    className="account-history-card"
                    sx={{
                        borderRadius: 'var(--order_button_radius)',
                        borderColor: 'var(--row_border)',
                        p: '24px',
                    }}
                >
                    <WalletHistory />
                </Paper>
            )}
            </Box>
        </Box>
        {/* <button className='rectangle content' disabled={!checkProfile(snapshot?.val())} style={{ background: '#01ABAE' }} onClick={onFinish}>Save</button> */}

        {/* Email Edit Dialog */}
        <EmailEditDialog
            open={emailEditDialogOpen}
            onClose={() => setEmailEditDialogOpen(false)}
            currentEmail={(profSnapshot.child('email').val() ?? emailAuth).trim()}
            onEmailUpdated={handleEmailUpdated}
        />

    </div>
}
